/**
 * AutocompleteTrigger
 * 
 * @Author: Focci
 * @Date: 2023-09-27 11:15:25
 * @Last Modified by: Focci
 * @Last Modified time: 2023-09-27 11:15:25
 */

import PropTypes from 'prop-types'
import clsx from 'clsx'
import SearchIcon from '@mui/icons-material/Search'
import useApp from '@hook/useApp'
import { ButtonBase } from '@comp/material'
import { useCallback } from 'react'
import { useTranslations } from 'next-intl'

function AutocompleteChips({ label, className = 'text-primary' }) {
  if(!label) return null
  
  return (
    <div 
      className={clsx([
        'bg-cf0 h-6 rounded-full px-3 flex items-center',
        'font-medium capitalize whitespace-nowrap', className
      ])}
    >
      {String(label).replace(/-/g, ' ')}
    </div>
  )
}

export default function AutocompleteTrigger({
  showSubmitButton = false,
  placeholder = '',
  className = 'w-full',
  labelCount = 3,
  onOpen = () => {},
  onSubmit = () => {},
  value = [],
  prefix,
  children,
}) {
  const { isMobile, locations } = useApp()
  const tt = useTranslations('track')
  const tc = useTranslations('common')
  const ts = useTranslations('search')
  const len = value.length

  const handleClick = useCallback((e) => {
    e.stopPropagation()
    onOpen(e)
  }, [onOpen])

  const handleSubmit = useCallback((e) => {
    e.stopPropagation()
    onSubmit()
  }, [onSubmit])

  return (
    <div
      role="button"
      tabIndex="0"
      onKeyPress={handleClick}
      onClick={handleClick}
      className={clsx([
        'border rounded-[.2rem] flex items-center justify-between px-1 outline-none',
        'min-h-10 max-w-full', className
      ])}
    >
      <div className="flex-auto p-2 flex items-center cursor-pointer overflow-hidden">
        {prefix || (
          <SearchIcon 
            className="text-helper shrink-0"
            sx={{ fontSize: '1.2rem' }} 
          />
        )}
        {len === 0 && (
          <div className="flex-auto grid">
            <div className="text-helper ml-1 whitespace-nowrap line-clamp-1 select-none">
              {placeholder}
            </div>
          </div>
        )}
        {len > 0 && (
          <div className="ml-2 flex flex-wrap gap-1">
            {value.slice(0, labelCount).map((v) => (
              <AutocompleteChips
                key={v.label}
                label={locations?.[`${v.type}_${v.value}`]?.zh || v.label}
                className="text-meta" 
              />
            ))}
            {len === 1 && !isMobile && (
              <AutocompleteChips label={`+${tt('add')}`} className="text-primary text-f.7" />
            )}
            {(len - labelCount) > 0 && (
              <AutocompleteChips label={`${len - labelCount}+`} className="text-primary text-f.7" />
            )}
          </div>
        )}
      </div>
      {children || (showSubmitButton && (
        <ButtonBase 
          className="text-f.8 rounded-[.2rem] shrink-0"
          onClick={handleSubmit}
        >
          <span className="py-1 px-3 whitespace-nowrap text-primary font-medium">
            {!isMobile ? tc('complete') : ts('search')}
          </span>
        </ButtonBase>
      ))}
    </div>
  )
}

AutocompleteTrigger.propTypes = {
  showSubmitButton: PropTypes.bool,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  labelCount: PropTypes.number,
  onOpen: PropTypes.func,
  onSubmit: PropTypes.func,
  value: PropTypes.array,
}
